// src/pages/admin/MemberList.jsx - UPDATED WITH BETTER ERROR HANDLING
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  User, 
  Phone, 
  Calendar, 
  Eye, 
  Lock,
  Unlock,
  Download,
  Search,
  Filter,
  LogIn,
  ChevronDown,
  ChevronUp,
  Package,
  Globe,
  DollarSign,
  Mail,
  CreditCard,
  Users,
  Share2,
  Shield,
  ShieldOff,
  PauseCircle,
  PlayCircle,
  AlertCircle,
  Check,
  X,
  Battery,
  TrendingUp,
  Wallet,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Edit,
  Save,
  CreditCard as PaymentIcon,
  Coins
} from 'lucide-react';

// Import from your centralized Firebase config file
import { database, ref, onValue, update, get, query, orderByChild, equalTo, set, push, auth } from '../../firebaseConfig';

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedMobileMember, setExpandedMobileMember] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [blockOptions, setBlockOptions] = useState({
    login: false,
    withdrawal: false,
    roi: false,
    levelIncome: false,
    internalTransfer: false,
    downlineActivation: false
  });
  
  // Currency block options
  const [currencyOptions, setCurrencyOptions] = useState({
    Bitcoin: 'active',
    'USDT (BEP-20)': 'active',
    'USDT (TRC-20)': 'active',
    Dogecoin: 'active',
    Ethereum: 'active',
    'Shiba Inu': 'active',
    Solana: 'active',
    TRON: 'active',
    USDC: 'active'
  });

  const currencies = [
    'Bitcoin',
    'USDT (BEP-20)',
    'USDT (TRC-20)',
    'Dogecoin',
    'Ethereum',
    'Shiba Inu',
    'Solana',
    'TRON',
    'USDC'
  ];

  const [processingAction, setProcessingAction] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  
  // State for Payment ID editing
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [editingPaymentMember, setEditingPaymentMember] = useState(null);
  const [paymentIdValue, setPaymentIdValue] = useState('');
  const [userPaymentIds, setUserPaymentIds] = useState({});
  const [loadingPaymentIds, setLoadingPaymentIds] = useState({});
  const [paymentUpdateSuccess, setPaymentUpdateSuccess] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMemberData, setEditingMemberData] = useState({ name: '', mobile: '' });
  const [savingMemberEdit, setSavingMemberEdit] = useState(false);
  const [memberEditHistory, setMemberEditHistory] = useState([]);
  const [loadingMemberEditHistory, setLoadingMemberEditHistory] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  
  // State to store unique products for dropdown
  const [uniqueProducts, setUniqueProducts] = useState([]);

  const [filters, setFilters] = useState({
    name: '',
    sponsorId: '',
    mobile: '',
    product: '',
    country: '',
    uplineSponsorId: ''
  });

  // Check authentication first
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Wait for auth state
        await new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
              console.log('User is authenticated:', user.uid);
              setAuthChecked(true);
            } else {
              console.log('No authenticated user');
              setError('Please login first');
            }
            unsubscribe();
            resolve();
          });
        });
      } catch (err) {
        console.error('Auth check error:', err);
        setError('Authentication failed');
      }
    };
    
    checkAuth();
  }, []);

  // Fetch users from Firebase
  const fetchData = async (showLoading = true) => {
    // Check if authenticated
    if (!auth.currentUser) {
      console.log('No authenticated user, waiting for auth...');
      // Wait a bit for auth to initialize
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (!auth.currentUser) {
        setError('Please login to view members');
        setLoading(false);
        return;
      }
    }

    if (showLoading) setLoading(true);
    setRefreshing(true);
    setError(null);
    
    try {
      console.log('Fetching users from Firebase...');
      const usersRef = ref(database, 'users');
      
      const snapshot = await get(usersRef);
      console.log('Snapshot exists:', snapshot.exists());
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('Data structure:', Object.keys(data));
        
        const userList = [];
        
        // Iterate through dates (like "2026-02-07")
        Object.keys(data).forEach(dateKey => {
          if (typeof data[dateKey] === 'object') {
            // Iterate through user IDs under each date
            Object.keys(data[dateKey]).forEach(userIdKey => {
              const user = data[dateKey][userIdKey];
              
              // Skip if not a valid user object
              if (!user || typeof user !== 'object' || user.role === 'admin') {
                return;
              }
              
              // Get member ID
              const memberId = user.userId || userIdKey;
              
              // Process packages
              let product = 'No Package';
              let activationDate = '';
              let packageDetails = null;
              let baseAmount = 0;
              let packageName = '';
              let roiStatus = 'active';
              let activePackageIndex = -1;
              let scheduleId = null;
              
              if (user.packages && Array.isArray(user.packages)) {
                activePackageIndex = user.packages.findIndex(pkg => pkg.status === 'active');
                if (activePackageIndex !== -1) {
                  const activePackage = user.packages[activePackageIndex];
                  product = activePackage.packagePlan || activePackage.packageId || 'Active Package';
                  packageName = activePackage.packageName || activePackage.packageId || 'Package';
                  baseAmount = parseFloat(activePackage.investmentAmount) || parseFloat(activePackage.baseAmount) || 0;
                  activationDate = activePackage.activationDate || '';
                  packageDetails = activePackage;
                  roiStatus = activePackage.roiStatus || 'active';
                  scheduleId = activePackage.scheduleId || activePackage.transactionId;
                } else if (user.packages.length > 0) {
                  const latestPackage = user.packages[user.packages.length - 1];
                  product = latestPackage.packagePlan || latestPackage.packageId || 'Package';
                  packageName = latestPackage.packageName || latestPackage.packageId || 'Package';
                  baseAmount = parseFloat(latestPackage.investmentAmount) || parseFloat(latestPackage.baseAmount) || 0;
                  activationDate = latestPackage.activationDate || '';
                  packageDetails = latestPackage;
                  roiStatus = latestPackage.roiStatus || 'active';
                  scheduleId = latestPackage.scheduleId || latestPackage.transactionId;
                }
              }
              
              // Get IP address and country
              let ipAddress = '';
              let loginCountry = '';
              
              if (user.lastLoginLocation) {
                ipAddress = user.lastLoginLocation.ip || user.lastLoginLocation.ipAddress || '';
                loginCountry = user.lastLoginLocation.country || user.country || '';
              } else {
                ipAddress = user.ipAddress || user.lastLoginIP || '';
                loginCountry = user.country || '';
              }
              
              // Get statuses with defaults
              const loginStatus = user.loginStatus || 'active';
              const withdrawalStatus = user.withdrawalStatus || 'enabled';
              const internalTransferStatus = user.internalTransferStatus || 'enabled';
              const downlineActivationStatus = user.downlineActivationStatus || 'enabled';
              const levelIncomeStatus = user.levelIncomeStatus || 'active';
              
              // Get currency statement status
              const currencyStatementStatus = user.currencyStatementStatus || {
                Bitcoin: 'active',
                'USDT (BEP-20)': 'active',
                'USDT (TRC-20)': 'active',
                Dogecoin: 'active',
                Ethereum: 'active',
                'Shiba Inu': 'active',
                Solana: 'active',
                TRON: 'active',
                USDC: 'active'
              };
              
              userList.push({
                id: userIdKey,
                userId: user.userId || userIdKey,
                memberId: memberId,
                fullName: user.name || '',
                name: user.name || '',
                userName: user.name || '',
                sponsorId: user.sponsorId || '',
                uplineSponsorId: user.uplineSponsorId || 'N/A',
                uplineName: 'N/A',
                ipAddress: ipAddress,
                loginCountry: loginCountry,
                packageDetails: packageDetails,
                packageName: packageName,
                baseAmount: baseAmount,
                password: user.password || '',
                product: product,
                mobile: user.mobile || '',
                country: user.country || '',
                dateOfJoining: user.registrationDate || dateKey,
                activationDate: activationDate,
                email: user.email || user.userId || '',
                status: user.status || 'active',
                role: user.role || 'user',
                balance: parseFloat(user.balance) || 0,
                deposited: parseFloat(user.deposited) || 0,
                totalEarnings: parseFloat(user.totalEarnings) || 0,
                withdrawal: parseFloat(user.withdrawal) || 0,
                withdrawalStatus: withdrawalStatus,
                referralCount: user.referralCount || 0,
                wallets: user.wallets || {},
                lastLogin: user.lastLogin || '',
                lastLoginLocation: user.lastLoginLocation || null,
                packages: user.packages || [],
                activePackageIndex: activePackageIndex,
                scheduleId: scheduleId,
                registrationTimestamp: user.registrationDate ? new Date(user.registrationDate).getTime() : new Date(dateKey).getTime(),
                createdAt: user.createdAt || user.registrationDate || dateKey,
                loginStatus: loginStatus,
                roiStatus: roiStatus,
                levelIncomeStatus: levelIncomeStatus,
                internalTransferStatus: internalTransferStatus,
                downlineActivationStatus: downlineActivationStatus,
                currencyStatementStatus: currencyStatementStatus,
                firebaseDateKey: dateKey,
                firebaseUserKey: userIdKey,
                firebaseUserPath: `users/${dateKey}/${userIdKey}`
              });
            });
          }
        });
        
        console.log(`Found ${userList.length} users`);
        
        // Sort by registration date in descending order
        const sortedUserList = userList.sort((a, b) => {
          const timeA = a.registrationTimestamp || new Date(a.createdAt || 0).getTime();
          const timeB = b.registrationTimestamp || new Date(b.createdAt || 0).getTime();
          return timeB - timeA;
        });
        
        const sponsorToNameMap = {};
        sortedUserList.forEach((member) => {
          const sponsorKey = String(member.sponsorId || '').trim();
          if (sponsorKey) sponsorToNameMap[sponsorKey] = member.fullName || member.name || member.userName || 'N/A';
        });

        const membersWithUplineName = sortedUserList.map((member) => {
          const uplineKey = String(member.uplineSponsorId || '').trim();
          return {
            ...member,
            userName: member.fullName || member.name || member.userName || '',
            uplineName: sponsorToNameMap[uplineKey] || 'N/A'
          };
        });

        setMembers(membersWithUplineName);
        
        // Extract unique products for dropdown
        const productsSet = new Set();
        membersWithUplineName.forEach(member => {
          if (member.packageName && member.product !== 'No Package') {
            const productLabel = `${member.packageName} ($${member.baseAmount})`;
            productsSet.add(productLabel);
          }
        });
        setUniqueProducts(Array.from(productsSet).sort());
        
        // After setting members, fetch payment IDs for all users
        await fetchAllPaymentIds(membersWithUplineName);
      } else {
        console.log('No users found in database');
        setMembers([]);
        setUniqueProducts([]);
      }
      setError(null);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.code === 'PERMISSION_DENIED') {
        setError("Permission denied. Please check Firebase rules.");
      } else if (error.message.includes('network')) {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("Failed to load members. Please try again.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Function to fetch payment IDs for all users from UserFundTransactions
  const fetchAllPaymentIds = async (userList) => {
    try {
      console.log('Fetching payment IDs...');
      const paymentIdsMap = {};
      
      // Get reference to UserFundTransactions
      const transactionsRef = ref(database, 'UserFundTransactions');
      const snapshot = await get(transactionsRef);
      
      if (snapshot.exists()) {
        const transactionsData = snapshot.val();
        console.log('UserFundTransactions keys:', Object.keys(transactionsData));
        
        // Iterate through all dates in UserFundTransactions
        Object.keys(transactionsData).forEach(dateKey => {
          const dateData = transactionsData[dateKey];
          
          if (dateData && typeof dateData === 'object') {
            Object.keys(dateData).forEach(sponsorId => {
              const userTransactions = dateData[sponsorId];
              
              if (userTransactions && typeof userTransactions === 'object') {
                Object.keys(userTransactions).forEach(transactionKey => {
                  const transaction = userTransactions[transactionKey];
                  
                  if (transaction && transaction.paymentId) {
                    if (!paymentIdsMap[sponsorId]) {
                      paymentIdsMap[sponsorId] = [];
                    }
                    
                    let timestamp = transaction.timestamp || transaction.lastFundDate;
                    if (!timestamp && transaction.timestampFormatted) {
                      timestamp = transaction.timestampFormatted;
                    }
                    
                    paymentIdsMap[sponsorId].push({
                      paymentId: transaction.paymentId,
                      transactionKey: transactionKey,
                      dateKey: dateKey,
                      sponsorId: sponsorId,
                      amount: parseFloat(transaction.amount) || 0,
                      userAddedFund: parseFloat(transaction.userAddedFund) || 0,
                      timestamp: timestamp,
                      description: transaction.description || '',
                      type: transaction.type || '',
                      paymentMethod: transaction.paymentMethod || '',
                      displayCurrency: transaction.displayCurrency || ''
                    });
                  }
                });
              }
            });
          }
        });
        
        // Sort payment IDs by timestamp
        Object.keys(paymentIdsMap).forEach(sponsorId => {
          paymentIdsMap[sponsorId].sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA;
          });
        });
        
        console.log(`Found payment IDs for ${Object.keys(paymentIdsMap).length} users`);
        setUserPaymentIds(paymentIdsMap);
      } else {
        console.log('No UserFundTransactions found');
      }
    } catch (error) {
      console.error("Error fetching payment IDs:", error);
    }
  };

  // Function to get the latest payment ID for a user
  const getLatestPaymentId = (sponsorId) => {
    if (!sponsorId || !userPaymentIds[sponsorId] || userPaymentIds[sponsorId].length === 0) {
      return 'N/A';
    }
    return userPaymentIds[sponsorId][0].paymentId;
  };

  // Function to get all transaction details for a user
  const getUserTransactions = (sponsorId) => {
    return userPaymentIds[sponsorId] || [];
  };

  // Function to handle payment ID edit
  const openPaymentDialog = (member, e) => {
    e.preventDefault();
    e.stopPropagation();
    const currentPaymentId = getLatestPaymentId(member.sponsorId);
    setEditingPaymentMember(member);
    setPaymentIdValue(currentPaymentId !== 'N/A' ? currentPaymentId : '');
    setShowPaymentDialog(true);
    setPaymentUpdateSuccess(false);
  };

  // Function to save edited payment ID
  const savePaymentId = async () => {
    if (!editingPaymentMember || !editingPaymentMember.sponsorId) return;
    
    setProcessingAction(true);
    
    try {
      const timestamp = new Date().toISOString();
      const currentDate = timestamp.split('T')[0];
      const currentTime = timestamp;
      
      const currentPaymentId = getLatestPaymentId(editingPaymentMember.sponsorId);
      const currentTransaction = userPaymentIds[editingPaymentMember.sponsorId] && userPaymentIds[editingPaymentMember.sponsorId].length > 0 
        ? userPaymentIds[editingPaymentMember.sponsorId][0] 
        : null;
      
      if (currentTransaction) {
        const transactionPath = `UserFundTransactions/${currentTransaction.dateKey}/${editingPaymentMember.sponsorId}/${currentTransaction.transactionKey}`;
        const transactionRef = ref(database, transactionPath);
        
        await update(transactionRef, {
          paymentId: paymentIdValue,
          lastUpdated: timestamp,
          updatedBy: 'admin'
        });
        
        const dateBasedEditRef = ref(database, `adminediteddetails/${currentDate}/${editingPaymentMember.sponsorId}_${Date.now()}`);
        
        const editDetails = {
          sponsorId: editingPaymentMember.sponsorId,
          memberName: editingPaymentMember.fullName,
          memberId: editingPaymentMember.memberId,
          previousPaymentId: currentPaymentId,
          newPaymentId: paymentIdValue,
          editedBy: 'admin',
          editedAt: timestamp,
          editedDate: currentDate,
          editedTime: currentTime,
          transactionDate: currentTransaction.dateKey,
          transactionKey: currentTransaction.transactionKey,
          transactionAmount: currentTransaction.amount || currentTransaction.userAddedFund,
          transactionDescription: currentTransaction.description,
          action: 'PAYMENT_ID_UPDATE',
          sourceFile: 'MemberList'
        };
        
        await set(dateBasedEditRef, editDetails);

        const paymentHistoryRef = ref(
          database,
          `memberProfileEditHistory/${editingPaymentMember.sponsorId || editingPaymentMember.memberId}_${Date.now()}_payment`
        );
        await set(paymentHistoryRef, editDetails);
        
        await fetchAllPaymentIds(members);
        await fetchMemberEditHistory();
        
        setPaymentUpdateSuccess(true);
        
        setTimeout(() => {
          setShowPaymentDialog(false);
          setEditingPaymentMember(null);
          setProcessingAction(false);
          setPaymentUpdateSuccess(false);
        }, 1500);
        
      } else {
        alert('No fund transaction found for this user. Cannot update payment ID.');
        setProcessingAction(false);
      }
    } catch (error) {
      console.error("Error updating payment ID:", error);
      alert(`Error: ${error.message}`);
      setProcessingAction(false);
    }
  };

  const cancelPaymentDialog = () => {
    setShowPaymentDialog(false);
    setEditingPaymentMember(null);
    setPaymentIdValue('');
    setPaymentUpdateSuccess(false);
    setProcessingAction(false);
  };

  useEffect(() => {
    if (authChecked) {
      fetchData();
      fetchMemberEditHistory();
    }
  }, [authChecked]);

  const fetchMemberEditHistory = async () => {
    try {
      setLoadingMemberEditHistory(true);
      const snapshot = await get(ref(database, 'memberProfileEditHistory'));
      const rows = [];

      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data || {}).forEach(([editKey, edit]) => {
          if (
            edit &&
            edit.sourceFile === 'MemberList' &&
            (edit.action === 'MEMBER_PROFILE_EDIT' || edit.action === 'PAYMENT_ID_UPDATE')
          ) {
            rows.push({
              id: editKey,
              ...edit
            });
          }
        });
      }

      rows.sort((a, b) => new Date(b.editedAt || b.date) - new Date(a.editedAt || a.date));
      setMemberEditHistory(rows);
    } catch (error) {
      console.error('Error fetching member profile edit history:', error);
      setMemberEditHistory([]);
    } finally {
      setLoadingMemberEditHistory(false);
    }
  };

  const startMemberEdit = (member, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingMemberId(member.id);
    setEditingMemberData({
      name: member.fullName || member.name || '',
      mobile: member.mobile || ''
    });
  };

  const cancelMemberEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingMemberId(null);
    setEditingMemberData({ name: '', mobile: '' });
    setSavingMemberEdit(false);
  };

  const saveMemberEdit = async (member, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!member?.firebaseUserPath) return;

    const newName = String(editingMemberData.name || '').trim();
    const newMobile = String(editingMemberData.mobile || '').trim();
    if (!newName || !newMobile) return;

    try {
      setSavingMemberEdit(true);
      const timestamp = new Date().toISOString();
      const userRef = ref(database, member.firebaseUserPath);

      await update(userRef, {
        name: newName,
        mobile: newMobile,
        updatedAt: timestamp,
        updatedBy: 'admin'
      });

      const historyRef = ref(
        database,
        `memberProfileEditHistory/${member.sponsorId || member.memberId}_${Date.now()}_profile`
      );

      await set(historyRef, {
        action: 'MEMBER_PROFILE_EDIT',
        sourceFile: 'MemberList',
        sponsorId: member.sponsorId || '',
        memberId: member.memberId || member.userId || member.id,
        memberName: member.fullName || member.name || '',
        previousName: member.fullName || member.name || '',
        newName,
        previousMobile: member.mobile || '',
        newMobile,
        editedBy: 'admin',
        editedAt: timestamp,
        editedDate: timestamp.split('T')[0],
        editedTime: timestamp
      });

      setMembers((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? { ...m, fullName: newName, name: newName, mobile: newMobile }
            : m
        )
      );

      setSuccessMessage('Member name and mobile updated successfully!');
      setShowSuccessDialog(true);
      cancelMemberEdit();
      fetchMemberEditHistory();
    } catch (error) {
      console.error('Error updating member profile:', error);
      setSavingMemberEdit(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  // Filter members based on filters
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      const textMatches = (text, searchTerm) => {
        if (!searchTerm) return true;
        if (!text) return false;
        return text.toString().toLowerCase().includes(searchTerm.toLowerCase());
      };

      const productMatches = () => {
        if (!filters.product) return true;
        if (!member.packageName || member.product === 'No Package') return false;
        const productLabel = `${member.packageName} ($${member.baseAmount})`;
        return productLabel === filters.product;
      };

      return (
        textMatches(member.fullName, filters.name) &&
        textMatches(member.sponsorId, filters.sponsorId) &&
        textMatches(member.mobile, filters.mobile) &&
        productMatches() &&
        textMatches(member.country, filters.country) &&
        textMatches(member.uplineSponsorId, filters.uplineSponsorId)
      );
    });
  }, [members, filters]);

  // Pagination calculations
  const indexOfLastMember = currentPage * itemsPerPage;
  const indexOfFirstMember = indexOfLastMember - itemsPerPage;
  const currentMembers = filteredMembers.slice(indexOfFirstMember, indexOfLastMember);
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0, rowIndex: null });

  const openBlockDialog = (member, event, rowIndex) => {
    setSelectedMember(member);
    setBlockOptions({
      login: member.loginStatus === 'blocked',
      withdrawal: member.withdrawalStatus === 'blocked',
      roi: member.roiStatus === 'blocked',
      levelIncome: member.levelIncomeStatus === 'blocked',
      internalTransfer: member.internalTransferStatus === 'blocked',
      downlineActivation: member.downlineActivationStatus === 'disabled'
    });
    
    if (member.currencyStatementStatus) {
      setCurrencyOptions({ ...member.currencyStatementStatus });
    } else {
      const defaultCurrencyOptions = {};
      currencies.forEach(currency => {
        defaultCurrencyOptions[currency] = 'active';
      });
      setCurrencyOptions(defaultCurrencyOptions);
    }
    
    const buttonRect = event.currentTarget.getBoundingClientRect();
    setPopupPosition({
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2,
      rowIndex: rowIndex
    });
    
    setShowBlockDialog(true);
  };

  const toggleCurrencyStatus = (currency) => {
    setCurrencyOptions(prev => ({
      ...prev,
      [currency]: prev[currency] === 'active' ? 'blocked' : 'active'
    }));
  };

  const applyBlockOptions = async () => {
    if (!selectedMember) return;
    
    try {
      setProcessingAction(true);
      const timestamp = new Date().toISOString();
      
      console.log('Starting block update for:', {
        userId: selectedMember.userId,
        fullName: selectedMember.fullName,
        scheduleId: selectedMember.scheduleId
      });

      // 1. Update user statuses in users collection
      const userUpdates = {};
      let hasUserUpdates = false;
      
      if (blockOptions.login !== (selectedMember.loginStatus === 'blocked')) {
        userUpdates.loginStatus = blockOptions.login ? 'blocked' : 'active';
        hasUserUpdates = true;
      }
      
      if (blockOptions.withdrawal !== (selectedMember.withdrawalStatus === 'blocked')) {
        userUpdates.withdrawalStatus = blockOptions.withdrawal ? 'blocked' : 'enabled';
        hasUserUpdates = true;
      }
      
      if (blockOptions.internalTransfer !== (selectedMember.internalTransferStatus === 'blocked')) {
        userUpdates.internalTransferStatus = blockOptions.internalTransfer ? 'blocked' : 'enabled';
        hasUserUpdates = true;
      }
      
      if (blockOptions.downlineActivation !== (selectedMember.downlineActivationStatus === 'disabled')) {
        userUpdates.downlineActivationStatus = blockOptions.downlineActivation ? 'disabled' : 'enabled';
        userUpdates.downlineActivationStatusUpdated = timestamp;
        userUpdates.downlineActivationStatusUpdatedBy = 'admin';
        hasUserUpdates = true;
      }
      
      if (blockOptions.levelIncome !== (selectedMember.levelIncomeStatus === 'blocked')) {
        userUpdates.levelIncomeStatus = blockOptions.levelIncome ? 'blocked' : 'active';
        userUpdates.levelIncomeStatusUpdated = timestamp;
        userUpdates.levelIncomeStatusUpdatedBy = 'admin';
        hasUserUpdates = true;
      }
      
      const currencyUpdates = {};
      let hasCurrencyUpdates = false;
      
      currencies.forEach(currency => {
        const currentStatus = selectedMember.currencyStatementStatus?.[currency] || 'active';
        if (currencyOptions[currency] !== currentStatus) {
          currencyUpdates[currency] = currencyOptions[currency];
          hasCurrencyUpdates = true;
        }
      });
      
      if (hasCurrencyUpdates) {
        userUpdates.currencyStatementStatus = {
          ...(selectedMember.currencyStatementStatus || {}),
          ...currencyUpdates
        };
        userUpdates.currencyStatementStatusUpdated = timestamp;
        userUpdates.currencyStatementStatusUpdatedBy = 'admin';
        hasUserUpdates = true;
      }
      
      if (hasUserUpdates) {
        const userRef = ref(database, selectedMember.firebaseUserPath);
        console.log('Updating user at:', selectedMember.firebaseUserPath);
        await update(userRef, userUpdates);
        
        const currentDate = timestamp.split('T')[0];
        
        const editDetails = {
          sponsorId: selectedMember.sponsorId,
          memberName: selectedMember.fullName,
          memberId: selectedMember.memberId,
          changes: userUpdates,
          editedBy: 'admin',
          editedAt: timestamp,
          editedDate: currentDate,
          editedTime: timestamp,
          action: 'STATUS_UPDATE'
        };
        
        const dateBasedEditRef = ref(database, `adminediteddetails/${currentDate}/${selectedMember.sponsorId}_${Date.now()}`);
        await set(dateBasedEditRef, editDetails);
      }

      // Update ROI status
      if (blockOptions.roi !== (selectedMember.roiStatus === 'blocked')) {
        const newRoiStatus = blockOptions.roi ? 'blocked' : 'active';
        console.log('Updating ROI status to:', newRoiStatus);
        
        if (selectedMember.activePackageIndex !== -1 && selectedMember.packages && selectedMember.packages[selectedMember.activePackageIndex]) {
          const packagePath = `${selectedMember.firebaseUserPath}/packages/${selectedMember.activePackageIndex}`;
          const packageRef = ref(database, packagePath);
          
          const packageSnapshot = await get(packageRef);
          const currentPackage = packageSnapshot.val() || {};
          
          const packageUpdates = {
            roiStatus: newRoiStatus,
            roiStatusUpdated: timestamp,
            roiStatusUpdatedBy: 'admin'
          };
          
          if (blockOptions.roi) {
            packageUpdates['blockCheck'] = {
              ...(currentPackage.blockCheck || {}),
              buyerBlocked: false,
              targetBlocked: true,
              directUplineBlocked: selectedMember.directUplineBlocked || false,
              roiBlocked: true,
              blockedAt: timestamp,
              blockedBy: 'admin',
              blockReason: 'ROI blocked by admin from Member List'
            };
          } else {
            packageUpdates['blockCheck'] = {
              ...(currentPackage.blockCheck || {}),
              buyerBlocked: false,
              targetBlocked: false,
              directUplineBlocked: selectedMember.directUplineBlocked || false,
              roiBlocked: false,
              unblockedAt: timestamp,
              unblockedBy: 'admin'
            };
          }
          
          await update(packageRef, packageUpdates);
          console.log('ROI status and blockCheck updated in user package');
        }
        
        // Update ROISchedule
        const roiScheduleRef = ref(database, 'ROISchedule');
        const roiSnapshot = await get(roiScheduleRef);
        
        if (roiSnapshot.exists()) {
          const roiData = roiSnapshot.val();
          const roiUpdatePromises = [];
          let scheduleFoundCount = 0;
          
          Object.keys(roiData).forEach(dateKey => {
            const dateData = roiData[dateKey];
            
            if (dateData) {
              Object.keys(dateData).forEach(userKey => {
                const userSchedules = dateData[userKey];
                
                if (userSchedules) {
                  Object.keys(userSchedules).forEach(scheduleKey => {
                    const schedule = userSchedules[scheduleKey];
                    
                    const isUserSchedule = 
                      schedule.userId === selectedMember.userId ||
                      schedule.buyerSponsorId === selectedMember.userId ||
                      schedule.sponsorId === selectedMember.userId ||
                      schedule.userName === selectedMember.fullName ||
                      schedule.scheduleId === selectedMember.scheduleId ||
                      schedule.transactionId === selectedMember.scheduleId;
                    
                    if (isUserSchedule) {
                      const scheduleRef = ref(database, `ROISchedule/${dateKey}/${userKey}/${scheduleKey}`);
                      
                      const updates = {
                        roiStatus: newRoiStatus,
                        lastUpdated: timestamp,
                        updatedBy: 'admin'
                      };
                      
                      if (blockOptions.roi) {
                        updates['blockCheck'] = {
                          buyerBlocked: false,
                          targetBlocked: true,
                          directUplineBlocked: selectedMember.directUplineBlocked || false,
                          roiBlocked: true,
                          blockedAt: timestamp,
                          blockedBy: 'admin',
                          blockReason: 'ROI blocked by admin from Member List'
                        };
                        
                        updates['blockStatus/isROIBlocked'] = true;
                        updates['blockStatus/userROIBlocked'] = true;
                        updates['blockStatus/packageROIBlocked'] = true;
                        updates['blockStatus/roiScheduleBlocked'] = true;
                      } else {
                        updates['blockCheck'] = {
                          buyerBlocked: false,
                          targetBlocked: false,
                          directUplineBlocked: selectedMember.directUplineBlocked || false,
                          roiBlocked: false,
                          unblockedAt: timestamp,
                          unblockedBy: 'admin'
                        };
                        
                        updates['blockStatus/isROIBlocked'] = false;
                        updates['blockStatus/userROIBlocked'] = false;
                        updates['blockStatus/packageROIBlocked'] = false;
                        updates['blockStatus/roiScheduleBlocked'] = false;
                      }
                      
                      roiUpdatePromises.push(update(scheduleRef, updates));
                      scheduleFoundCount++;
                      console.log(`Found ROI schedule: ${dateKey}/${userKey}/${scheduleKey}`);
                    }
                  });
                }
              });
            }
          });
          
          if (roiUpdatePromises.length > 0) {
            await Promise.all(roiUpdatePromises);
            console.log(`Updated ${scheduleFoundCount} ROI schedules in ROISchedule with blockCheck`);
          } else {
            console.log('No ROI schedules found in ROISchedule collection');
          }
        }
        
        const currentDate = timestamp.split('T')[0];
        const editDetails = {
          sponsorId: selectedMember.sponsorId,
          memberName: selectedMember.fullName,
          memberId: selectedMember.memberId,
          previousRoiStatus: selectedMember.roiStatus,
          newRoiStatus: newRoiStatus,
          editedBy: 'admin',
          editedAt: timestamp,
          editedDate: currentDate,
          editedTime: timestamp,
          action: 'ROI_STATUS_UPDATE',
          blockCheckUpdated: true
        };
        
        const dateBasedEditRef = ref(database, `adminediteddetails/${currentDate}/${selectedMember.sponsorId}_${Date.now()}_roi`);
        await set(dateBasedEditRef, editDetails);
      }
      
      setSuccessMessage('Member status updated successfully!');
      setShowSuccessDialog(true);
      setShowBlockDialog(false);
      
      setTimeout(() => {
        setShowSuccessDialog(false);
        fetchData(false);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating member status:', error);
      setSuccessMessage(`Error: ${error.message}`);
      setShowSuccessDialog(true);
    } finally {
      setProcessingAction(false);
    }
  };

  const autoLoginAsUser = (userData) => {
    localStorage.setItem('autoLoginUserId', userData.sponsorId || userData.userId);
    localStorage.setItem('autoLoginUserName', userData.fullName || userData.name);
    localStorage.setItem('autoLoginUserData', JSON.stringify({
      ...userData,
      userId: userData.sponsorId || userData.userId,
      sponsorId: userData.sponsorId,
      name: userData.fullName || userData.name
    }));

    localStorage.setItem(
      'aerovoltAuth',
      JSON.stringify({
        role: userData.role || 'user',
        userId: userData.sponsorId || userData.userId,
        userName: userData.fullName || userData.name,
        dbKey: userData.firebaseUserKey || userData.id || '',
        registrationDate: userData.firebaseDateKey || userData.registrationDate || ''
      })
    );
    
    const loginToken = `auto_${Date.now()}_${Math.random().toString(36).substr(2)}`;
    localStorage.setItem('autoLoginToken', loginToken);
    
    const userDashboardUrl = `/user-dashboard?autoLogin=true&token=${loginToken}`;
    window.open(userDashboardUrl, '_blank');
  };

  const exportToCSV = () => {
    const headers = ['Sr.No', 'Member Login', 'Sponsor ID', 'Upline Sponsor ID', 'Full Name', 'IP Address', 'IP Country', 'Payment ID', 'Product', 'Mobile No.', 'Country', 'Date of Joining', 'Activation Date', 'Login Status', 'Withdrawal Status', 'ROI Status', 'Level Income Status', 'Internal Transfer Status', 'Downline Activation Status'];
    
    const csvData = filteredMembers.map((member, index) => [
      index + 1,
      member.memberId,
      member.sponsorId,
      member.uplineSponsorId,
      member.fullName,
      member.ipAddress || '',
      member.loginCountry || '',
      getLatestPaymentId(member.sponsorId),
      `${member.packageName} ($${member.baseAmount})`,
      member.mobile,
      member.country,
      formatDate(member.dateOfJoining),
      formatDate(member.activationDate),
      member.loginStatus,
      member.withdrawalStatus,
      member.roiStatus,
      member.levelIncomeStatus,
      member.internalTransferStatus,
      member.downlineActivationStatus
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return '';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      
      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
      return '';
    }
  };

  const toggleRowExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleMobileMember = (id) => {
    setExpandedMobileMember(expandedMobileMember === id ? null : id);
  };

  const StatusBadge = ({ status, type }) => {
    const isBlocked = status === 'blocked' || status === 'disabled';
    const isActive = status === 'active' || status === 'enabled';
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${
        isBlocked 
          ? 'bg-red-500/20 text-red-400 border-red-500/30' 
          : 'bg-green-500/20 text-green-400 border-green-500/30'
      }`}>
        {type === 'login' && <Shield className="h-2.5 w-2.5 mr-1" />}
        {type === 'withdrawal' && <DollarSign className="h-2.5 w-2.5 mr-1" />}
        {type === 'roi' && <TrendingUp className="h-2.5 w-2.5 mr-1" />}
        {type === 'level' && <Users className="h-2.5 w-2.5 mr-1" />}
        {type === 'transfer' && <Wallet className="h-2.5 w-2.5 mr-1" />}
        {type === 'downline' && <Users className="h-2.5 w-2.5 mr-1" />}
        {type === 'currency' && <Coins className="h-2.5 w-2.5 mr-1" />}
        {status}
      </span>
    );
  };

  const PaymentIdCell = ({ member }) => {
    const paymentId = getLatestPaymentId(member.sponsorId);
    const transactions = getUserTransactions(member.sponsorId);
    
    return (
      <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
        <div className="relative group">
          <div className={`text-[10px] font-mono ${paymentId !== 'N/A' ? 'text-green-400' : 'text-gray-400'}`}>
            {paymentId}
          </div>
          {transactions.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
              <div className="bg-gray-800 border border-gray-700 rounded p-1 text-[8px] whitespace-nowrap">
                {transactions.map((t, i) => (
                  <div key={i} className="text-gray-300">
                    {i === 0 && 'Latest: '}
                    {t.paymentId} (${t.amount || t.userAddedFund}) - {formatDate(t.timestamp)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {paymentId !== 'N/A' && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openPaymentDialog(member, e);
            }}
            className="p-0.5 hover:bg-primary/20 text-primary rounded"
            title="Edit Payment ID"
          >
            <Edit className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    );
  };

  const PaymentEditDialog = () => {
    if (!showPaymentDialog || !editingPaymentMember) return null;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <h3 className="text-md font-bold text-white">Edit Payment ID</h3>
            <button
              onClick={cancelPaymentDialog}
              className="text-gray-400 hover:text-white transition-colors"
              disabled={processingAction}
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="p-6">
            {!paymentUpdateSuccess ? (
              <>
                <div className="mb-4">
                  <h4 className="text-white font-bold text-sm mb-1">{editingPaymentMember.fullName}</h4>
                  <p className="text-gray-400 text-xs">ID: {editingPaymentMember.memberId}</p>
                  <p className="text-gray-400 text-xs">Sponsor ID: {editingPaymentMember.sponsorId}</p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Payment ID
                  </label>
                  <input
                    type="text"
                    value={paymentIdValue}
                    onChange={(e) => setPaymentIdValue(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-primary"
                    placeholder="Enter Payment ID"
                    autoFocus
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={cancelPaymentDialog}
                    disabled={processingAction}
                    className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-xl text-white font-bold text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={savePaymentId}
                    disabled={processingAction || !paymentIdValue.trim()}
                    className="flex-1 py-3 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-bold text-sm transition-all"
                  >
                    {processingAction ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </div>
                    ) : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="text-green-400" size={32} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Success!</h3>
                <p className="text-gray-300">Payment ID updated successfully</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const CurrencyStatusBadge = ({ currency, status }) => {
    const isBlocked = status === 'blocked';
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs border ${
        isBlocked 
          ? 'bg-red-500/20 text-red-400 border-red-500/30' 
          : 'bg-green-500/20 text-green-400 border-green-500/30'
      }`}>
        <Coins className="h-3 w-3 mr-1" />
        {currency}: {status}
      </span>
    );
  };

  const BlockDialog = () => {
    if (!selectedMember || !showBlockDialog) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div 
          className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999
          }}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
            <h3 className="text-md font-bold text-white">Block/Unblock Member Features</h3>
            <button
              onClick={() => setShowBlockDialog(false)}
              className="text-gray-400 hover:text-white transition-colors"
              disabled={processingAction}
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-white font-bold text-sm mb-1">{selectedMember.fullName}</h4>
              <p className="text-gray-400 text-xs">ID: {selectedMember.userId}</p>
              <p className="text-gray-400 text-xs">Sponsor ID: {selectedMember.sponsorId}</p>
              <p className="text-gray-400 text-xs mt-1">Package: {selectedMember.packageName || 'No Package'}</p>
              <p className="text-gray-400 text-xs">Schedule ID: {selectedMember.scheduleId || 'N/A'}</p>
              <p className="text-gray-400 text-xs">Payment ID: {getLatestPaymentId(selectedMember.sponsorId)}</p>
            </div>
            
            {/* Main Features Section */}
            <div className="mb-6">
              <h4 className="text-white font-semibold text-sm mb-3 flex items-center">
                <Shield className="h-4 w-4 mr-2 text-primary" />
                Main Features
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {/* Login Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">Login Status</div>
                    <div className="text-gray-400 text-xs">Allow/Disallow user login</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.loginStatus === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.loginStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, login: !prev.login}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.login ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.login ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>

                {/* Withdrawal Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">Withdrawal Status</div>
                    <div className="text-gray-400 text-xs">Allow/Disallow withdrawals</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.withdrawalStatus === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.withdrawalStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, withdrawal: !prev.withdrawal}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.withdrawal ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.withdrawal ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>

                {/* ROI Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">ROI Status</div>
                    <div className="text-gray-400 text-xs">Enable/Disable ROI earnings</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.roiStatus === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.roiStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, roi: !prev.roi}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.roi ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.roi ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>

                {/* Level Income Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">Level Income Status</div>
                    <div className="text-gray-400 text-xs">Enable/Disable level commissions</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.levelIncomeStatus === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.levelIncomeStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, levelIncome: !prev.levelIncome}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.levelIncome ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.levelIncome ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>

                {/* Internal Transfer Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">Internal Transfer Status</div>
                    <div className="text-gray-400 text-xs">Allow/Disallow wallet transfers</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.internalTransferStatus === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.internalTransferStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, internalTransfer: !prev.internalTransfer}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.internalTransfer ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.internalTransfer ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>

                {/* Downline Activation Status */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <div className="text-white font-medium text-sm">Downline Activation Status</div>
                    <div className="text-gray-400 text-xs">Allow/Disallow downline activations</div>
                    <div className="text-xs mt-1">Current: 
                      <span className={`ml-1 ${selectedMember.downlineActivationStatus === 'disabled' ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedMember.downlineActivationStatus || 'enabled'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setBlockOptions(prev => ({...prev, downlineActivation: !prev.downlineActivation}))}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${blockOptions.downlineActivation ? 'bg-red-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${blockOptions.downlineActivation ? 'transform translate-x-6' : ''}`}></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Currency Statement Status Section */}
            <div className="mb-6">
              <h4 className="text-white font-semibold text-sm mb-3 flex items-center">
                <Coins className="h-4 w-4 mr-2 text-primary" />
                Currency Statement Status
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {currencies.map((currency) => (
                  <div key={currency} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                    <div>
                      <div className="text-white font-medium text-sm">{currency}</div>
                      <div className="text-xs mt-1">Current: 
                        <span className={`ml-1 ${(selectedMember.currencyStatementStatus?.[currency] || 'active') === 'blocked' ? 'text-red-400' : 'text-green-400'}`}>
                          {selectedMember.currencyStatementStatus?.[currency] || 'active'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleCurrencyStatus(currency)}
                      className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${currencyOptions[currency] === 'blocked' ? 'bg-red-500' : 'bg-green-500'}`}
                    >
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${currencyOptions[currency] === 'blocked' ? 'transform translate-x-6' : ''}`}></div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Buttons at bottom */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-800 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowBlockDialog(false)}
                disabled={processingAction}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-xl text-white font-bold text-sm"
              >
                Cancel
              </button>
              <button
                onClick={applyBlockOptions}
                disabled={processingAction}
                className="flex-1 py-3 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-bold text-sm transition-all"
              >
                {processingAction ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SuccessDialog = () => {
    if (!showSuccessDialog) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-400" size={32} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Success!</h3>
            <p className="text-gray-300 mb-6">{successMessage}</p>
            <button
              onClick={() => setShowSuccessDialog(false)}
              className="w-full py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DesktopTableRow = ({ member, index, globalIndex }) => (
    <React.Fragment key={member.id}>
      <tr className="hover:bg-secondary/10 transition-colors">
        {/* Sr. No */}
        <td className="py-2 px-1 text-center">
          <div className="text-gray-400 font-mono text-xs">
            {globalIndex + 1}
          </div>
        </td>

        {/* Member Login */}
        <td className="py-2 px-1">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center mb-1">
              <User className="h-3 w-3 text-blue-400" />
            </div>
            
            <div className="text-[9px] font-medium text-white font-body truncate max-w-[60px]">
              {member.memberId}
            </div>
            
            <button
              onClick={() => autoLoginAsUser(member)}
              className="flex items-center text-[9px] text-primary hover:text-primary/80 mt-1"
              title="Login as this user"
            >
              <LogIn className="h-2 w-2 mr-1" />
              Login
            </button>
          </div>
        </td>

        {/* Sponsor ID */}
        <td className="py-2 px-1">
          <div className="text-white text-xs text-center truncate max-w-[70px]">
            {member.sponsorId || ''}
          </div>
        </td>

        {/* Sponsor Name */}
        <td className="py-2 px-1">
          {editingMemberId === member.id ? (
            <div className="flex items-center justify-center gap-1">
              <input
                type="text"
                value={editingMemberData.name}
                onChange={(e) =>
                  setEditingMemberData((prev) => ({ ...prev, name: e.target.value }))
                }
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-[70px] bg-secondary/50 border border-border rounded px-1 py-0.5 text-[10px] text-white"
              />
              <button
                onClick={(e) => saveMemberEdit(member, e)}
                disabled={savingMemberEdit}
                className="p-0.5 hover:bg-green-500/20 text-green-400 rounded disabled:opacity-50"
                title="Save Profile"
              >
                <Save className="h-2.5 w-2.5" />
              </button>
              <button
                onClick={cancelMemberEdit}
                disabled={savingMemberEdit}
                className="p-0.5 hover:bg-gray-500/20 text-gray-300 rounded disabled:opacity-50"
                title="Cancel Edit"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1">
              <div className="text-white text-xs text-center truncate max-w-[70px]">
                {member.userName || member.fullName || 'N/A'}
              </div>
              <button
                onClick={(e) => startMemberEdit(member, e)}
                className="p-0.5 hover:bg-primary/20 text-primary rounded"
                title="Edit Name & Mobile"
              >
                <Edit className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
        </td>

        {/* Upline Sponsor ID */}
        <td className="py-2 px-1">
          <div className="text-white text-xs text-center truncate max-w-[70px]">
            {member.uplineSponsorId || 'N/A'}
          </div>
        </td>

        {/* Upline Sponsor Name */}
        <td className="py-2 px-1">
          <div className="flex items-center justify-center">
            <div className="text-white text-xs text-center truncate max-w-[90px]">
              {member.uplineName || 'N/A'}
            </div>
          </div>
        </td>

        {/* IP Address */}
        <td className="py-2 px-1">
          <div className="text-gray-400 text-xs font-mono text-center truncate max-w-[90px]">
            {member.ipAddress ? (
              <div className="text-primary text-xs">{member.ipAddress}</div>
            ) : (
              <div className="text-gray-400 text-xs">N/A</div>
            )}
          </div>
        </td>

        {/* IP Country */}
        <td className="py-2 px-1">
          <div className="text-gray-400 text-xs text-center">
            {member.loginCountry ? (
              <div className="inline-flex items-center px-1 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-[10px]">
                <Globe className="h-2 w-2 mr-1 text-purple-400" />
                <span className="text-white">{member.loginCountry}</span>
              </div>
            ) : (
              <div className="text-gray-400 text-xs">N/A</div>
            )}
          </div>
        </td>

        {/* Product */}
        <td className="py-2 px-1">
          <div className={`inline-flex items-center px-1 py-0.5 rounded text-xs ${
            member.product !== 'No Package'
              ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30'
              : 'bg-red-500/20 border border-red-500/30'
          }`}>
            <Package className="h-2 w-2 mr-1" />
            <div className="flex flex-col">
              <div className="font-medium text-white text-[10px] truncate max-w-[60px]">
                {member.packageName || member.product}
              </div>
              {member.product !== 'No Package' && (
                <div className="text-[8px] text-gray-400">
                  ${member.baseAmount.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Mobile No. */}
        <td className="py-2 px-1">
          {editingMemberId === member.id ? (
            <div className="flex items-center justify-center gap-1">
              <input
                type="text"
                value={editingMemberData.mobile}
                onChange={(e) =>
                  setEditingMemberData((prev) => ({ ...prev, mobile: e.target.value }))
                }
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-[78px] bg-secondary/50 border border-border rounded px-1 py-0.5 text-[10px] text-white"
              />
              <button
                onClick={(e) => saveMemberEdit(member, e)}
                disabled={savingMemberEdit}
                className="p-0.5 hover:bg-green-500/20 text-green-400 rounded disabled:opacity-50"
                title="Save Profile"
              >
                <Save className="h-2.5 w-2.5" />
              </button>
              <button
                onClick={cancelMemberEdit}
                disabled={savingMemberEdit}
                className="p-0.5 hover:bg-gray-500/20 text-gray-300 rounded disabled:opacity-50"
                title="Cancel Edit"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1">
              <div className="text-white text-xs text-center truncate max-w-[70px]">
                {member.mobile || ''}
              </div>
              <button
                onClick={(e) => startMemberEdit(member, e)}
                className="p-0.5 hover:bg-primary/20 text-primary rounded"
                title="Edit Name & Mobile"
              >
                <Edit className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
        </td>

        {/* Date of Joining */}
        <td className="py-2 px-1">
          <div className="text-white text-xs text-center">
            {formatDate(member.dateOfJoining) || 'N/A'}
          </div>
        </td>

        {/* Status Columns */}
        <td className="py-2 px-1">
          <div className="flex flex-col items-center space-y-1">
            <StatusBadge status={member.loginStatus} type="login" />
            <StatusBadge status={member.withdrawalStatus} type="withdrawal" />
          </div>
        </td>

        <td className="py-2 px-1">
          <div className="flex flex-col items-center space-y-1">
            <StatusBadge status={member.roiStatus} type="roi" />
            <StatusBadge status={member.levelIncomeStatus} type="level" />
          </div>
        </td>

        {/* Action */}
        <td className="py-2 px-1 relative">
          <div className="flex flex-col items-center space-y-1">
            <button
              onClick={(e) => openBlockDialog(member, e, globalIndex)}
              className="w-6 h-6 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 flex items-center justify-center transition-colors"
              title="Block/Unblock Member Features"
            >
              <Shield className="h-3 w-3 text-red-400" />
            </button>
            
            <button
              onClick={() => toggleRowExpand(member.id)}
              className="w-6 h-6 rounded-full bg-secondary/50 hover:bg-secondary/70 border border-border flex items-center justify-center transition-colors"
              title={expandedRow === member.id ? "Hide Details" : "Show Details"}
            >
              {expandedRow === member.id ? (
                <ChevronUp className="h-3 w-3 text-gray-400" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-400" />
              )}
            </button>
          </div>
        </td>
      </tr>
      
      {/* Expanded Row Details */}
      {expandedRow === member.id && (
        <tr className="bg-secondary/5">
          <td colSpan="17" className="py-3 px-3">
            <div className="bg-secondary/20 border border-border/30 rounded-xl p-3 card-glow">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Account Information */}
                <div className="space-y-2">
                  <h4 className="text-gray-400 font-semibold text-xs flex items-center font-heading">
                    <User className="h-3 w-3 mr-2" />
                    Account Info
                  </h4>
                  <div className="space-y-1">
                    <div>
                      <label className="text-xs text-gray-400 font-body">Email</label>
                      <div className="text-xs text-white truncate">{member.email || ''}</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Password</label>
                      <div className="text-xs text-white font-mono">{member.password}</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Status</label>
                      <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                        member.status === 'active'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {member.status}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Referral Count</label>
                      <div className="text-xs text-white flex items-center">
                        <Users className="h-2.5 w-2.5 mr-1 text-primary" />
                        {member.referralCount || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Information */}
                <div className="space-y-2">
                  <h4 className="text-gray-400 font-semibold text-xs flex items-center font-heading">
                    <Package className="h-3 w-3 mr-2" />
                    Package Info
                  </h4>
                  <div className="space-y-1">
                    <div>
                      <label className="text-xs text-gray-400 font-body">Package Name</label>
                      <div className="text-xs font-bold text-white">
                        {member.packageName || 'No Package'}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Base Amount</label>
                      <div className="text-xs text-white flex items-center">
                        ${member.baseAmount.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Upline Sponsor ID</label>
                      <div className="text-xs text-white flex items-center">
                        {member.uplineSponsorId || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-body">Upline Name</label>
                      <div className="text-xs text-white flex items-center">
                        {member.uplineName || 'N/A'}
                      </div>
                    </div>
                    {member.activationDate && (
                      <div>
                        <label className="text-xs text-gray-400 font-body">Activation Date</label>
                        <div className="text-xs text-white">{formatDate(member.activationDate)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Block Status Information */}
                <div className="space-y-2">
                  <h4 className="text-gray-400 font-semibold text-xs flex items-center font-heading">
                    <Shield className="h-3 w-3 mr-2" />
                    Block Status
                  </h4>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Login:</span>
                      <StatusBadge status={member.loginStatus} type="login" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Withdrawal:</span>
                      <StatusBadge status={member.withdrawalStatus} type="withdrawal" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">ROI:</span>
                      <StatusBadge status={member.roiStatus} type="roi" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Level Income:</span>
                      <StatusBadge status={member.levelIncomeStatus} type="level" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Internal Transfer:</span>
                      <StatusBadge status={member.internalTransferStatus} type="transfer" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Downline Activation:</span>
                      <StatusBadge status={member.downlineActivationStatus || 'enabled'} type="downline" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  const MobileAccordionItem = ({ member, index, globalIndex }) => (
    <div key={member.id} className="card-glow mb-4 overflow-hidden">
      <div 
        className="p-4 bg-secondary hover:bg-secondary/80 transition-colors cursor-pointer flex justify-between items-center"
        onClick={() => toggleMobileMember(member.id)}
      >
        <div className="flex items-center gap-3">
          <div className="text-gray-400 font-body text-sm font-mono">#{globalIndex + 1}</div>
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center">
                <User className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <div className="font-bold text-white font-heading text-sm">{member.fullName}</div>
                <div className="text-xs text-gray-400 font-body">
                  ID: {member.memberId} | Sponsor: {member.sponsorId}
                </div>
                {member.uplineSponsorId && member.uplineSponsorId !== 'N/A' && (
                  <div className="text-[10px] text-primary mt-0.5 flex items-center">
                    <Share2 className="h-2.5 w-2.5 mr-1" />
                    Upline: {member.uplineSponsorId} ({member.uplineName || 'N/A'})
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openBlockDialog(member, e, globalIndex);
            }}
            className="w-8 h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 flex items-center justify-center transition-colors"
            title="Block/Unblock Member"
          >
            <Shield className="h-4 w-4 text-red-400" />
          </button>
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
            expandedMobileMember === member.id ? 'rotate-180' : ''
          }`} />
        </div>
      </div>
      
      {expandedMobileMember === member.id && (
        <div className="p-4 border-t border-border">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Member ID</div>
              <div className="text-sm text-white font-bold font-heading">{member.memberId}</div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Name</div>
              {editingMemberId === member.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editingMemberData.name}
                    onChange={(e) =>
                      setEditingMemberData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full bg-secondary/70 border border-border rounded px-2 py-1 text-xs text-white"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => saveMemberEdit(member, e)}
                      disabled={savingMemberEdit}
                      className="flex-1 px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-[11px] disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelMemberEdit}
                      disabled={savingMemberEdit}
                      className="flex-1 px-2 py-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 rounded text-[11px] disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-white font-heading truncate">{member.userName || member.fullName || 'N/A'}</div>
                  <button
                    onClick={(e) => startMemberEdit(member, e)}
                    className="p-1 hover:bg-primary/20 text-primary rounded"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Sponsor ID</div>
              <div className="text-sm text-white font-heading">{member.sponsorId}</div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Upline Sponsor</div>
              <div className="text-sm text-white font-heading flex items-center">
                <Share2 className="h-3 w-3 mr-1 text-primary" />
                {member.uplineSponsorId || 'N/A'}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Upline Name</div>
              <div className="text-sm text-white font-heading">
                {member.uplineName || 'N/A'}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Status</div>
              <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                member.status === 'active'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {member.status}
              </div>
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Mobile</div>
              {editingMemberId === member.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editingMemberData.mobile}
                    onChange={(e) =>
                      setEditingMemberData((prev) => ({ ...prev, mobile: e.target.value }))
                    }
                    className="w-full bg-secondary/70 border border-border rounded px-2 py-1 text-xs text-white"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => saveMemberEdit(member, e)}
                      disabled={savingMemberEdit}
                      className="flex-1 px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-[11px] disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelMemberEdit}
                      disabled={savingMemberEdit}
                      className="flex-1 px-2 py-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 rounded text-[11px] disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-white font-heading flex items-center truncate">
                    <Phone className="h-3 w-3 mr-1 text-primary" />
                    {member.mobile || 'N/A'}
                  </div>
                  <button
                    onClick={(e) => startMemberEdit(member, e)}
                    className="p-1 hover:bg-primary/20 text-primary rounded"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="bg-secondary/50 p-3 rounded-lg">
              <div className="text-xs text-gray-400 font-body mb-1">Referrals</div>
              <div className="text-sm text-white font-heading flex items-center">
                <Users className="h-3 w-3 mr-1 text-primary" />
                {member.referralCount || 0}
              </div>
            </div>
          </div>

          {/* Package Info */}
          <div className="mb-4">
            <h4 className="text-gray-400 font-semibold text-xs flex items-center mb-2 font-heading">
              <Package className="h-3 w-3 mr-2" />
              Package Information
            </h4>
            <div className="bg-secondary/50 border border-border rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <div className="text-xs text-gray-400 font-body">Package</div>
                  <div className="text-sm font-bold text-white font-heading">
                    {member.packageName || 'No Package'}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs ${
                  member.product !== 'No Package'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                }`}>
                  ${member.baseAmount.toFixed(2)}
                </div>
              </div>
              <div className="mt-2">
                <div className="text-xs text-gray-400 font-body">Upline Sponsor ID</div>
                <div className="text-xs text-white flex items-center mt-1">
                  <Share2 className="h-2.5 w-2.5 mr-1 text-primary" />
                  {member.uplineSponsorId || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Block Status */}
          <div className="mb-4">
            <h4 className="text-gray-400 font-semibold text-xs flex items-center mb-2 font-heading">
              <Shield className="h-3 w-3 mr-2" />
              Block Status
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">Login</div>
                <StatusBadge status={member.loginStatus} type="login" />
              </div>
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">Withdrawal</div>
                <StatusBadge status={member.withdrawalStatus} type="withdrawal" />
              </div>
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">ROI</div>
                <StatusBadge status={member.roiStatus} type="roi" />
              </div>
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">Level Income</div>
                <StatusBadge status={member.levelIncomeStatus} type="level" />
              </div>
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">Internal Transfer</div>
                <StatusBadge status={member.internalTransferStatus} type="transfer" />
              </div>
              <div className="bg-secondary/50 p-2 rounded-lg">
                <div className="text-xs text-gray-400 font-body mb-1">Downline Activation</div>
                <StatusBadge status={member.downlineActivationStatus || 'enabled'} type="downline" />
              </div>
            </div>
          </div>

          {/* Currency Statement Status */}
          <div className="mb-4">
            <h4 className="text-gray-400 font-semibold text-xs flex items-center mb-2 font-heading">
              <Coins className="h-3 w-3 mr-2" />
              Currency Statement Status
            </h4>
            <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto">
              {currencies.map((currency) => (
                <div key={currency} className="flex items-center justify-between bg-secondary/50 p-2 rounded-lg">
                  <span className="text-xs text-gray-400 font-body">{currency}</span>
                  <StatusBadge status={member.currencyStatementStatus?.[currency] || 'active'} type="currency" />
                </div>
              ))}
            </div>
          </div>

          {/* Account Details */}
          <div className="mb-4">
            <h4 className="text-gray-400 font-semibold text-xs flex items-center mb-2 font-heading">
              <User className="h-3 w-3 mr-2" />
              Account Details
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-400 font-body">Password:</span>
                <span className="text-xs text-white font-mono">{member.password}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-400 font-body">Email:</span>
                <span className="text-xs text-white truncate">{member.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-400 font-body">Joining Date:</span>
                <span className="text-xs text-white">{formatDate(member.dateOfJoining)}</span>
              </div>
              {member.activationDate && (
                <div className="flex justify-between">
                  <span className="text-xs text-gray-400 font-body">Activation Date:</span>
                  <span className="text-xs text-green-400">{formatDate(member.activationDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-border">
            <button
              onClick={() => autoLoginAsUser(member)}
              className="flex-1 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors font-body flex items-center justify-center text-sm"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Login as User
            </button>
            <button
              onClick={(e) => openBlockDialog(member, e, globalIndex)}
              className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors font-body flex items-center justify-center text-sm"
            >
              <Shield className="h-4 w-4 mr-2" />
              Block Options
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="text-center py-20">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-white mb-2">{error}</h3>
          <p className="text-gray-400">Please check your Firebase configuration and try again.</p>
          <button 
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchData();
            }}
            className="mt-4 px-6 py-2 bg-primary hover:bg-primary/80 text-white font-semibold rounded-xl transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-full mx-auto px-4">
        {/* Payment Edit Dialog */}
        <PaymentEditDialog />
        
        {/* Dialogs */}
        <BlockDialog />
        <SuccessDialog />
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white font-heading">Member List</h1>
              <p className="text-gray-400 mt-1 text-sm lg:text-base font-body">
                Showing {indexOfFirstMember + 1}-{Math.min(indexOfLastMember, filteredMembers.length)} of {filteredMembers.length} members (100 per page)
              </p>
            </div>
            <button
              onClick={() => fetchData(false)}
              disabled={refreshing}
              className="px-4 py-2 bg-secondary/20 hover:bg-secondary/30 text-gray-400 rounded-lg transition-colors border border-border flex items-center justify-center text-sm font-body"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-secondary/30 border border-border rounded-2xl p-4 lg:p-6 mb-6 card-glow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Filter className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-white font-heading">Filter</h2>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setFilters({
                    name: '',
                    sponsorId: '',
                    mobile: '',
                    product: '',
                    country: '',
                    uplineSponsorId: ''
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-secondary/20 hover:bg-secondary/30 text-gray-400 rounded-lg transition-colors border border-border flex items-center justify-center text-sm font-body"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear
              </button>
              <button
                onClick={exportToCSV}
                disabled={filteredMembers.length === 0}
                className={`px-4 py-2 rounded-lg transition-all flex items-center justify-center text-sm border ${
                  filteredMembers.length === 0
                    ? 'bg-gray-600 text-gray-400 border-border cursor-not-allowed'
                    : 'btn-electric text-white font-semibold'
                }`}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Desktop Filters */}
          <div className="hidden lg:grid grid-cols-5 gap-4">
            {/* Full Name */}
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Full Name:</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Full Name"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>

            {/* Sponsor ID */}
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Sponsor ID:</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="sponsorId"
                  value={filters.sponsorId}
                  onChange={handleFilterChange}
                  placeholder="Sponsor ID"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>

            {/* Upline Sponsor ID */}
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Upline Sponsor ID:</label>
              <div className="relative">
                <Share2 className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="uplineSponsorId"
                  value={filters.uplineSponsorId}
                  onChange={handleFilterChange}
                  placeholder="Upline Sponsor ID"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Mobile No.:</label>
              <div className="relative">
                <Phone className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="mobile"
                  value={filters.mobile}
                  onChange={handleFilterChange}
                  placeholder="Mobile"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>

            {/* Product Dropdown */}
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Product:</label>
              <div className="relative">
                <Package className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 z-10" />
                <select
                  name="product"
                  value={filters.product}
                  onChange={handleFilterChange}
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white focus:outline-none focus:border-primary text-xs font-body appearance-none"
                >
                  <option value="">All Products</option>
                  {uniqueProducts.map((product, index) => (
                    <option key={index} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Country */}
            <div className="col-span-2">
              <label className="block text-gray-400 mb-1 text-xs font-body">Country:</label>
              <div className="relative">
                <Globe className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="country"
                  value={filters.country}
                  onChange={handleFilterChange}
                  placeholder="Country"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>
          </div>

          {/* Mobile Filters */}
          <div className="lg:hidden grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Name/ID:</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Name or Sponsor ID"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-400 mb-1 text-xs font-body">Upline ID:</label>
              <div className="relative">
                <Share2 className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  name="uplineSponsorId"
                  value={filters.uplineSponsorId}
                  onChange={handleFilterChange}
                  placeholder="Upline Sponsor ID"
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary text-xs font-body"
                />
              </div>
            </div>
            
            <div className="col-span-2">
              <label className="block text-gray-400 mb-1 text-xs font-body">Product:</label>
              <div className="relative">
                <Package className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 z-10" />
                <select
                  name="product"
                  value={filters.product}
                  onChange={handleFilterChange}
                  className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-white focus:outline-none focus:border-primary text-xs font-body appearance-none"
                >
                  <option value="">All Products</option>
                  {uniqueProducts.map((product, index) => (
                    <option key={index} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block card-glow overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-8">Sr.</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Member Login</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Sponsor ID</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Sponsor Name</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Upline Sponsor ID</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Upline Sponsor Name</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-28">IP Address</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-20">IP Country</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Product</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Mobile</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-20">Join Date</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-32">Login/Withdrawal</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-32">ROI/Level Status</th>
                  <th className="py-2 px-1 text-center text-gray-400 font-semibold text-xs font-heading w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {currentMembers.length > 0 ? (
                  currentMembers.map((member, index) => (
                    <DesktopTableRow 
                      key={member.id} 
                      member={member} 
                      index={index}
                      globalIndex={indexOfFirstMember + index} 
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" className="py-16 text-center">
                      <div className="text-gray-400 text-6xl mb-4">👥</div>
                      <h3 className="text-xl font-semibold text-white mb-2 font-heading">
                        {members.length === 0 ? 'No Members in Database' : 'No Members Found'}
                      </h3>
                      <p className="text-gray-400 font-body">
                        {members.length === 0 
                          ? 'There are no members in the database.' 
                          : 'Try adjusting your search filters'}
                      </p>
                      {members.length > 0 && (
                        <button
                          onClick={() => {
                            setFilters({
                              name: '',
                              sponsorId: '',
                              mobile: '',
                              product: '',
                              country: '',
                              uplineSponsorId: ''
                            });
                            setCurrentPage(1);
                          }}
                          className="mt-4 px-6 py-2 btn-electric text-white font-semibold rounded-xl transition-all"
                        >
                          Clear All Filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - Desktop */}
          {filteredMembers.length > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between p-4 border-t border-border/30 bg-secondary/20">
              <div className="text-sm text-gray-400 mb-4 md:mb-0">
                Page {currentPage} of {totalPages} • Showing {indexOfFirstMember + 1}-{Math.min(indexOfLastMember, filteredMembers.length)} of {filteredMembers.length} members
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={firstPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="First Page"
                >
                  <ChevronsLeft className="h-4 w-4 text-gray-400" />
                </button>
                
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                          currentPage === pageNum
                            ? 'bg-primary text-white font-bold'
                            : 'bg-secondary/50 hover:bg-secondary text-gray-400'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
                
                <button
                  onClick={lastPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Last Page"
                >
                  <ChevronsRight className="h-4 w-4 text-gray-400" />
                </button>
              </div>
              
              <div className="hidden md:block text-sm text-gray-400">
                {itemsPerPage} per page
              </div>
            </div>
          )}
        </div>

        {/* Mobile Accordion View */}
        <div className="lg:hidden">
          {currentMembers.length > 0 ? (
            currentMembers.map((member, index) => (
              <MobileAccordionItem 
                key={member.id} 
                member={member} 
                index={index}
                globalIndex={indexOfFirstMember + index}
              />
            ))
          ) : (
            <div className="card-glow p-8 text-center">
              <div className="text-gray-400 text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold text-white mb-2 font-heading">
                {members.length === 0 ? 'No Members in Database' : 'No Members Found'}
              </h3>
              <p className="text-gray-400 font-body">
                {members.length === 0 
                  ? 'There are no members in the database.' 
                  : 'Try adjusting your search filters'}
              </p>
              {members.length > 0 && (
                <button
                  onClick={() => {
                    setFilters({
                      name: '',
                      sponsorId: '',
                      mobile: '',
                      product: '',
                      country: '',
                      uplineSponsorId: ''
                    });
                    setCurrentPage(1);
                  }}
                  className="mt-4 px-6 py-2 btn-electric text-white font-semibold rounded-xl transition-all"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}

          {/* Pagination Controls - Mobile */}
          {filteredMembers.length > 0 && (
            <div className="flex flex-col items-center justify-center mt-6 space-y-4">
              <div className="text-sm text-gray-400 text-center">
                Page {currentPage} of {totalPages} • {indexOfFirstMember + 1}-{Math.min(indexOfLastMember, filteredMembers.length)} of {filteredMembers.length} members
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={firstPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="First Page"
                >
                  <ChevronsLeft className="h-4 w-4 text-gray-400" />
                </button>
                
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage === 1) {
                      pageNum = i + 1;
                    } else if (currentPage === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = currentPage - 1 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                          currentPage === pageNum
                            ? 'bg-primary text-white font-bold'
                            : 'bg-secondary/50 hover:bg-secondary text-gray-400'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
                
                <button
                  onClick={lastPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-secondary/50 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Last Page"
                >
                  <ChevronsRight className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Member Profile Edit History */}
        <div className="card-glow overflow-hidden mt-8">
          <div className="p-4 border-b border-border bg-secondary/30">
            <h3 className="text-white font-semibold text-lg font-heading">Member Edit History</h3>
            <p className="text-gray-400 text-xs font-body">Name, mobile, and payment ID updates made from Member List</p>
          </div>
          {loadingMemberEditHistory ? (
            <div className="p-4 text-gray-400 text-sm">Loading history...</div>
          ) : memberEditHistory.length === 0 ? (
            <div className="p-4 text-gray-400 text-sm">No profile edits yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">Edited At</th>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">Action</th>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">Sponsor ID</th>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">Member</th>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">Previous</th>
                    <th className="py-2 px-3 text-left text-gray-400 text-xs font-heading">New</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {memberEditHistory.slice(0, 30).map((row) => (
                    <tr key={row.id} className="hover:bg-secondary/10">
                      <td className="py-2 px-3 text-xs text-gray-300">{formatDateTime(row.editedAt || row.date)}</td>
                      <td className="py-2 px-3 text-xs text-cyan-400">{row.action || '-'}</td>
                      <td className="py-2 px-3 text-xs text-cyan-400">{row.sponsorId || 'N/A'}</td>
                      <td className="py-2 px-3 text-xs text-white">{row.memberName || row.memberId || 'N/A'}</td>
                      {row.action === 'PAYMENT_ID_UPDATE' ? (
                        <>
                          <td className="py-2 px-3 text-xs text-red-400 font-mono">{row.previousPaymentId || '-'}</td>
                          <td className="py-2 px-3 text-xs text-green-400 font-mono">{row.newPaymentId || '-'}</td>
                        </>
                      ) : (
                        <>
                          <td className="py-2 px-3 text-xs">
                            <div><span className="text-gray-400">Name:</span> <span className="text-red-400">{row.previousName || '-'}</span></div>
                            <div><span className="text-gray-400">Mobile:</span> <span className="text-red-400">{row.previousMobile || '-'}</span></div>
                          </td>
                          <td className="py-2 px-3 text-xs">
                            <div><span className="text-gray-400">Name:</span> <span className="text-green-400">{row.newName || '-'}</span></div>
                            <div><span className="text-gray-400">Mobile:</span> <span className="text-green-400">{row.newMobile || '-'}</span></div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MemberList;